import config from '../config'
import { invokeApig } from './awsLib'
import Cache from './Cache'

export default class AuthUser {
	constructor(handle_token_changed) {
		this.username = null
		this.id_token = null
		this.access_token = null
		this.refresh_token = null
		this.expires_in = null
		this.expires_date = null

		this.handle_token_changed = handle_token_changed

		this._load_state()
	}

	async authorize(code) {
		if (!code) {
			window.location.href = config.cognito.IDP_URL
			return
		}

		const body = new URLSearchParams()
		body.append('client_id', config.cognito.APP_CLIENT_ID)
		body.append('redirect_uri', config.cognito.IDP_REDIRECT_URL)
		body.append('code', code)
		body.append('grant_type', 'authorization_code')

		const headers = new Headers()
		headers.append('Access-Control-Allow-Origin', '*')
		headers.append('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')

		const token_response = await fetch(`https://${config.cognito.IDP_DOMAIN}.auth.us-east-1.amazoncognito.com/oauth2/token`,
			{
				method: 'POST',
				mode: "cors",
				referrer: "no-referrer",
				credentials: 'include',
				headers: headers,
				body:body
			}
		)
		const tokens = await token_response.json()

		if (tokens.error) {
			window.location.href = config.cognito.IDP_URL
			return
		}

		this.id_token = tokens.id_token
		this.access_token = tokens.access_token
		this.expires_in = tokens.expires_in
		this.refresh_token = tokens.refresh_token
		this.expires_date = new Date((new Date()).getTime() + (parseInt(this.expires_in) * 1000)).getTime()

		await this.get_user_info()

		const team_check = await invokeApig({
			path: `/users/user-lookup?username=${this.username}`
		}, this.id_token)

		if (team_check && team_check.user_property && team_check.user_property.teams.length > 0) {
			this._store_state()

			if (this.handle_token_changed) {
				this.handle_token_changed()
			}
		} else {
			await this.signout()
			throw new Error('No Teams')
		}
	}

	async get_id_token() {
		if (!this.is_authenticated()) {
			return null
		}

		const now = new Date()
		if (now < new Date(this.expires_date)) {
			return this.id_token
		}

		const body = new URLSearchParams()
		body.append('client_id', config.cognito.APP_CLIENT_ID)
		body.append('grant_type', 'refresh_token')
		body.append('refresh_token', this.refresh_token)

		const headers = new Headers()
		headers.append('Access-Control-Allow-Origin', '*')
		headers.append('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')

		const token_response = await fetch(`https://${config.cognito.IDP_DOMAIN}.auth.us-east-1.amazoncognito.com/oauth2/token`,
			{
				method: 'POST',
				mode: "cors",
				referrer: "no-referrer",
				credentials: 'include',
				headers: headers,
				body:body
			}
		)
		const tokens = await token_response.json()
		if (tokens.error) {
			window.location.href = config.cognito.IDP_URL
			return
		}

		this.id_token = tokens.id_token
		this.access_token = tokens.access_token
		this.expires_in = tokens.expires_in
		this.expires_date = new Date((new Date()).getTime() + (parseInt(this.expires_in) * 1000)).getTime()

		this._store_state()

		if (this.handle_token_changed) {
			this.handle_token_changed()
		}
		return this.id_token
	}

	async get_user_info() {
		const headers = new Headers()
		headers.append('Authorization', `bearer ${this.access_token}`)

		const info_response = await fetch(`https://${config.cognito.IDP_DOMAIN}.auth.us-east-1.amazoncognito.com/oauth2/userInfo`,
			{
				method: 'GET',
				mode: "cors",
				headers: headers
			}
		)
		const user_info = await info_response.json()

		if (user_info.error) {
			window.location.href = config.cognito.IDP_URL
			return
		}

		this.username = user_info.email
    this._store_state()

		return {
      username: user_info.email,
      admin: user_info['custom:admin'] === "1" ? true : false
		}
  }

  async is_admin() {
		const headers = new Headers()
		headers.append('Authorization', `bearer ${this.access_token}`)

		const info_response = await fetch(`https://${config.cognito.IDP_DOMAIN}.auth.us-east-1.amazoncognito.com/oauth2/userInfo`,
			{
				method: 'GET',
				mode: "cors",
				headers: headers
			}
		)

    const user_info = await info_response.json()
    if (user_info.error) {
      return false
    }

    return user_info['custom:admin'] === "1"
  }

	is_authenticated() {
		return this.id_token !== null
	}

	async signout() {
		//todo: IDP Signout
		const cache = new Cache(null)
		cache.clear()

		this._clear_state()
	}

	_load_state() {
		// not in session storage
		if (!sessionStorage.getItem('AuthUser:username')) {
			return
		}

		this.username = sessionStorage.getItem('AuthUser:username')
		this.id_token = sessionStorage.getItem('AuthUser:id_token')
		this.access_token = sessionStorage.getItem('AuthUser:access_token')
		this.refresh_token = sessionStorage.getItem('AuthUser:refresh_token')
		this.expires_in = sessionStorage.getItem('AuthUser:expires_in')

		if (sessionStorage.getItem('AuthUser:expires_date')) {
			this.expires_date = parseInt(sessionStorage.getItem('AuthUser:expires_date'))
		} else {
			this.expires_date = null
		}
	}

	_store_state() {
		sessionStorage.setItem('AuthUser:username', this.username)
		sessionStorage.setItem('AuthUser:id_token', this.id_token)
		sessionStorage.setItem('AuthUser:access_token', this.access_token)
		sessionStorage.setItem('AuthUser:refresh_token', this.refresh_token)
		sessionStorage.setItem('AuthUser:expires_in', this.expires_in)
		sessionStorage.setItem('AuthUser:expires_date', this.expires_date)
	}

	_clear_state() {
		sessionStorage.removeItem('AuthUser:username')
		sessionStorage.removeItem('AuthUser:id_token')
		sessionStorage.removeItem('AuthUser:access_token')
		sessionStorage.removeItem('AuthUser:refresh_token')
		sessionStorage.removeItem('AuthUser:expires_in')
		sessionStorage.removeItem('AuthUser:expires_date')

		this.username = null
		this.id_token = null
		this.access_token = null
		this.refresh_token = null
		this.expires_in = null
		this.expires_date = null
	}
}